import * as Popover from '@radix-ui/react-popover';
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import {
  DayPicker,
  type ChevronProps,
  type DateRange as RdpDateRange,
} from 'react-day-picker';
import 'react-day-picker/style.css';
import CalendarIcon from '../../assets/icons/calendar.svg?react';
import ChevronDownIcon from '../../assets/icons/chevron_down.svg?react';
import { useLayoutTier } from '../../hooks/useLayoutTier';
import { cn } from '../../utils/className';
import { TimeRangeSheet } from './TimeRangeSheet';

function NavChevron({ orientation, size = 16 }: ChevronProps) {
  const isLeft = orientation === 'left';
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d={isLeft ? 'M10.5 3.5L5.5 8l5 4.5' : 'M5.5 3.5L10.5 8l-5 4.5'}
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

export type TimeRange =
  | '30m'
  | '60m'
  | '12h'
  | '24h'
  | '7d'
  | '30d'
  | '6m'
  | '1y'
  | 'custom';

export interface CustomDateRange {
  from: Date;
  to: Date;
}

type PresetRange = Exclude<TimeRange, 'custom'>;
export type PresetOption =
  | 'last30Minutes'
  | 'last60Minutes'
  | 'last12Hours'
  | 'today'
  | 'lastWeek'
  | 'lastMonth'
  | 'custom';

const PRESET_LABELS: Record<PresetRange, string> = {
  '30m': 'Last 30 minutes',
  '60m': 'Last 60 minutes',
  '12h': 'Last 12 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '6m': 'Last 6 months',
  '1y': 'Last year',
};

const OPTION_LABELS: Record<PresetOption, string> = {
  last30Minutes: 'Last 30 minutes',
  last60Minutes: 'Last 60 minutes',
  last12Hours: 'Last 12 hours',
  today: 'Today',
  lastWeek: 'Last week',
  lastMonth: 'Last month',
  custom: 'Custom',
};

const OPTIONS = (Object.keys(OPTION_LABELS) as PresetOption[]).map((value) => ({
  value,
  label: OPTION_LABELS[value],
}));

const OPTION_TO_RANGE: Record<Exclude<PresetOption, 'custom'>, TimeRange> = {
  last30Minutes: '30m',
  last60Minutes: '60m',
  last12Hours: '12h',
  today: '24h',
  lastWeek: '7d',
  lastMonth: '30d',
};

const TRIGGER_CLASS_NAME = cn(
  'size-p2 flex items-center gap-2 rounded-sm px-2 py-1.5',
  'border-border-primary bg-surface-primary border',
  'text-primary cursor-pointer transition-colors',
  'hover:bg-surface-focus-support focus-visible:outline-none',
  'focus-visible:border-border-focus'
);

const HEADING_TRIGGER_CLASS_NAME = cn(
  'text-secondary flex min-w-0 items-center gap-2 rounded-sm',
  'cursor-pointer focus-visible:outline-none',
  'focus-visible:ring-border-focus focus-visible:ring-2'
);

/** A short bar under the day number. */
export const MARKED_DAY_CLASS_NAME = `[&>button]:relative
  [&>button]:after:absolute [&>button]:after:top-[calc(50%+10px)]
  [&>button]:after:left-1/2 [&>button]:after:h-1 [&>button]:after:w-6
  [&>button]:after:-translate-x-1/2 [&>button]:after:rounded-full
  [&>button]:after:bg-surface-focus`;

const DAY_PICKER_STYLE = {
  '--rdp-day-height': '2.25rem',
  '--rdp-day-width': '2.25rem',
  '--rdp-day_button-height': '2.1rem',
  '--rdp-day_button-width': '2.6rem',
  '--rdp-nav_button-height': '1.5rem',
  '--rdp-nav_button-width': '1.5rem',
  '--rdp-nav-height': '2rem',
} as CSSProperties;

interface TimeRangeSelectorProps {
  selectedRange: TimeRange;
  customRange?: CustomDateRange;
  onRangeChange: (range: TimeRange) => void;
  onCustomRangeChange: (range: CustomDateRange) => void;
  /** `heading` shows the range as a page title with a calendar icon. */
  appearance?: 'field' | 'heading';
  /** Days the calendar underlines, such as days the machine was sorting. */
  markedDays?: Date[];
}

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toTimeString(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function getTriggerLabel(
  range: TimeRange,
  customRange?: CustomDateRange
): string {
  if (range !== 'custom') {
    const labels: Partial<Record<TimeRange, string>> = {
      '24h': 'Today',
      '7d': 'Last week',
      '30d': 'Last month',
    };
    return labels[range] ?? PRESET_LABELS[range as PresetRange];
  }
  if (!customRange) return 'Custom range';

  const fmtDate = (d: Date) =>
    d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  const fmtTime = (d: Date) =>
    d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

  const sameDay =
    toDateString(customRange.from) === toDateString(customRange.to);
  if (sameDay && isWholeDay(customRange)) {
    return fmtDate(customRange.from);
  }
  if (sameDay) {
    return `${fmtDate(customRange.from)} ${fmtTime(customRange.from)}–${fmtTime(customRange.to)}`;
  }
  return `${fmtDate(customRange.from)} – ${fmtDate(customRange.to)}`;
}

const START_OF_DAY = '00:00';
const END_OF_DAY = '23:59';

/** A day picked in the calendar runs to 23:59, or to now when it is today. */
function isWholeDay({ from, to }: CustomDateRange): boolean {
  const endsAtMidnight = toTimeString(to) === END_OF_DAY;
  const endsNow = toDateString(to) === toDateString(new Date());
  return toTimeString(from) === START_OF_DAY && (endsAtMidnight || endsNow);
}

function combineDateAndTime(date: Date, timeStr: string): Date {
  const [h, m] = timeStr.split(':').map(Number);
  const result = new Date(date);
  result.setHours(h, m, 0, 0);
  return result;
}

function toDisplayDate(d: Date): string {
  return d.toLocaleDateString('en-GB').replace(/\//g, '.');
}

function clampToNow(date: Date): Date {
  const now = new Date();
  return date > now ? now : date;
}

function getOptionFromRange(range: TimeRange): PresetOption {
  switch (range) {
    case '30m':
      return 'last30Minutes';
    case '60m':
      return 'last60Minutes';
    case '12h':
      return 'last12Hours';
    case '24h':
      return 'today';
    case '7d':
      return 'lastWeek';
    case '30d':
      return 'lastMonth';
    default:
      return 'custom';
  }
}

function buildPresetRange(option: Exclude<PresetOption, 'custom'>): {
  from: Date;
  to: Date;
} {
  const now = new Date();

  if (option === 'today') {
    const from = new Date(now);
    from.setHours(0, 0, 0, 0);
    return { from, to: now };
  }

  const from = new Date(now);
  from.setMilliseconds(0);

  switch (option) {
    case 'last30Minutes':
      from.setMinutes(from.getMinutes() - 30);
      break;
    case 'last60Minutes':
      from.setMinutes(from.getMinutes() - 60);
      break;
    case 'last12Hours':
      from.setHours(from.getHours() - 12);
      break;
    case 'lastWeek':
      from.setDate(from.getDate() - 7);
      break;
    case 'lastMonth':
      from.setDate(from.getDate() - 30);
      break;
  }

  return { from, to: now };
}

function buildNextDateRange(
  currentRange: RdpDateRange | undefined,
  selectedDate: Date
): RdpDateRange {
  if (!currentRange?.from || currentRange.to) {
    return { from: selectedDate, to: undefined };
  }

  if (selectedDate < currentRange.from) {
    return { from: selectedDate, to: currentRange.from };
  }

  return { from: currentRange.from, to: selectedDate };
}

export const TimeRangeSelector = memo(function TimeRangeSelector({
  selectedRange,
  customRange,
  onRangeChange,
  onCustomRangeChange,
  appearance = 'field',
  markedDays,
}: TimeRangeSelectorProps) {
  const { isPhone } = useLayoutTier();
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState<PresetOption>('today');

  const defaultCustom = useMemo<CustomDateRange>(() => {
    if (customRange) return customRange;
    const to = new Date();
    const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
    return { from, to };
  }, [customRange]);

  const [dateRange, setDateRange] = useState<RdpDateRange | undefined>({
    from: defaultCustom.from,
    to: defaultCustom.to,
  });
  const [fromTime, setFromTime] = useState(toTimeString(defaultCustom.from));
  const [toTime, setToTime] = useState(toTimeString(defaultCustom.to));

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        const option =
          selectedRange === 'custom'
            ? 'custom'
            : getOptionFromRange(selectedRange);

        setSelection(option);

        if (option === 'custom' && customRange) {
          setDateRange({ from: customRange.from, to: customRange.to });
          setFromTime(toTimeString(customRange.from));
          setToTime(toTimeString(customRange.to));
        } else if (option !== 'custom') {
          const presetRange = buildPresetRange(option);
          setDateRange({ from: presetRange.from, to: presetRange.to });
          setFromTime(toTimeString(presetRange.from));
          setToTime(toTimeString(presetRange.to));
        } else {
          setDateRange({ from: defaultCustom.from, to: defaultCustom.to });
          setFromTime(toTimeString(defaultCustom.from));
          setToTime(toTimeString(defaultCustom.to));
        }
      }
    },
    [selectedRange, customRange, defaultCustom]
  );

  const resolvedFrom = dateRange?.from
    ? combineDateAndTime(dateRange.from, fromTime)
    : undefined;
  const resolvedTo = dateRange?.to
    ? combineDateAndTime(dateRange.to, toTime)
    : undefined;
  const clampedTo = resolvedTo ? clampToNow(resolvedTo) : undefined;

  const isApplyDisabled =
    !resolvedFrom || !clampedTo || resolvedFrom >= clampedTo;
  const showOrderError =
    isApplyDisabled && !!dateRange?.from && !!dateRange?.to;

  const applyCustomRange = useCallback(() => {
    if (!resolvedFrom || !clampedTo) return;
    if (resolvedFrom >= clampedTo) return;

    onCustomRangeChange({ from: resolvedFrom, to: clampedTo });

    if (selection !== 'custom') {
      onRangeChange(OPTION_TO_RANGE[selection]);
    } else {
      onRangeChange('custom');
    }

    setOpen(false);
  }, [resolvedFrom, clampedTo, onCustomRangeChange, onRangeChange, selection]);

  const handleOptionSelect = useCallback((option: PresetOption) => {
    setSelection(option);

    if (option === 'custom') {
      return;
    }

    const presetRange = buildPresetRange(option);
    setDateRange({ from: presetRange.from, to: presetRange.to });
    setFromTime(toTimeString(presetRange.from));
    setToTime(toTimeString(presetRange.to));
  }, []);

  const handleDateSelect = useCallback(
    (
      _nextRange: RdpDateRange | undefined,
      selectedDate: Date,
      modifiers: { disabled?: boolean }
    ) => {
      if (modifiers.disabled) {
        return;
      }

      // A second tap ends the range. On the same day, it selects that day.
      const nextRange = buildNextDateRange(dateRange, selectedDate);
      setDateRange(nextRange);
      setFromTime(START_OF_DAY);
      setToTime(END_OF_DAY);

      if (selection !== 'custom') {
        setSelection('custom');
      }
    },
    [dateRange, selection]
  );

  const handleFromTimeChange = useCallback(
    (time: string) => {
      setFromTime(time);
      if (selection !== 'custom') {
        setSelection('custom');
      }
    },
    [selection]
  );

  const handleToTimeChange = useCallback(
    (time: string) => {
      setToTime(time);
      if (selection !== 'custom') {
        setSelection('custom');
      }
    },
    [selection]
  );

  const today = new Date();
  const fromDate = dateRange?.from
    ? toDisplayDate(dateRange.from)
    : '--.--.----';
  const tillDate = dateRange?.to ? toDisplayDate(dateRange.to) : '-';
  const triggerLabel = getTriggerLabel(selectedRange, customRange);
  const triggerClassName =
    appearance === 'heading' ? HEADING_TRIGGER_CLASS_NAME : TRIGGER_CLASS_NAME;
  const triggerContent =
    appearance === 'heading' ? (
      <>
        <CalendarIcon aria-hidden className='h-4 w-4 shrink-0' />
        <span className='size-p1 truncate font-bold'>{triggerLabel}</span>
        <ChevronDownIcon
          aria-hidden
          className='h-5 w-5 shrink-0 [&_path]:fill-current'
        />
      </>
    ) : (
      <>
        <span className='truncate'>{triggerLabel}</span>
        <ChevronDownIcon className='h-4 w-4 shrink-0' />
      </>
    );

  if (isPhone) {
    return (
      <>
        <button
          type='button'
          onClick={() => handleOpenChange(true)}
          className={triggerClassName}
        >
          {triggerContent}
        </button>
        <TimeRangeSheet
          open={open}
          onOpenChange={handleOpenChange}
          options={OPTIONS}
          selectedOption={selection}
          onOptionSelect={handleOptionSelect}
          dateRange={dateRange}
          onDateSelect={handleDateSelect}
          markedDays={markedDays}
          fromDate={fromDate}
          tillDate={tillDate}
          fromTime={fromTime}
          toTime={toTime}
          onFromTimeChange={handleFromTimeChange}
          onToTimeChange={handleToTimeChange}
          showOrderError={showOrderError}
          isApplyDisabled={isApplyDisabled}
          onApply={applyCustomRange}
        />
      </>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button className={triggerClassName}>{triggerContent}</button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align='end'
          sideOffset={4}
          className={cn(
            'z-9999 overflow-hidden rounded-lg',
            'border-border-primary bg-surface-primary border shadow-md',
            'animate-in fade-in-0 zoom-in-95',
            'w-[calc(100dvw-1rem)] max-w-[calc(100dvw-1rem)]',
            'sm:w-fit sm:max-w-[34rem]',
            'focus:outline-none'
          )}
        >
          <div
            className='flex w-full max-w-full flex-col overflow-hidden
              sm:flex-row'
          >
            <div
              className={cn(
                'bg-surface-primary border-border-primary w-full border-b p-3',
                'flex flex-col gap-1',
                'sm:w-44 sm:border-r sm:border-b-0'
              )}
            >
              {OPTIONS.map(({ value: option, label }) => (
                <button
                  key={option}
                  onClick={() => handleOptionSelect(option)}
                  className={cn(
                    `size-p2 w-full cursor-pointer rounded-lg px-3 py-2
                    text-left`,
                    'transition-colors',
                    selection === option
                      ? `text-brand-primary bg-surface-primary-support
                        font-bold`
                      : 'text-primary hover:bg-surface-primary-support/70'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className='flex w-full flex-col p-3 sm:w-78'>
              <div className='w-full'>
                <DayPicker
                  mode='range'
                  selected={dateRange}
                  onSelect={handleDateSelect}
                  disabled={{ after: today }}
                  modifiers={{ marked: markedDays ?? [] }}
                  modifiersClassNames={{ marked: MARKED_DAY_CLASS_NAME }}
                  defaultMonth={dateRange?.from}
                  navLayout='around'
                  numberOfMonths={1}
                  fixedWeeks
                  components={{ Chevron: NavChevron }}
                  style={{
                    ...DAY_PICKER_STYLE,
                    width: '100%',
                    maxWidth: '100%',
                  }}
                  styles={{
                    root: { width: '100%', maxWidth: '100%' },
                    months: { width: '100%', maxWidth: '100%' },
                    month: { width: '100%' },
                    month_grid: { width: '100%' },
                    table: { width: '100%' },
                  }}
                  className={cn(
                    'w-full min-w-0',
                    '[&_.rdp-root]:w-full',
                    '[&_.rdp-months]:w-full',
                    '[&_.rdp-month]:w-full',
                    '[&_.rdp-table]:w-full',
                    '[&_.rdp-month_caption]:justify-center',
                    '[&_.rdp-month_caption]:mb-1',
                    '[&_.rdp-nav]:w-full',
                    '[&_.rdp-nav]:justify-between',
                    '[&_.rdp-caption_label]:text-center',
                    '[&_.rdp-weekdays]:mb-0.5',
                    '[&_.rdp-week]:my-0.5',
                    '[&_.rdp-weekday]:text-center'
                  )}
                />
              </div>

              <div className='bg-border-primary mt-1 h-px' />

              <div className='mt-2.5 flex flex-col gap-2'>
                <div
                  className='grid grid-cols-[4rem_minmax(0,1fr)_auto]
                    items-center gap-2'
                >
                  <span className='size-p2 text-secondary'>From</span>
                  <span
                    className='size-p2 text-primary min-w-0 truncate font-bold'
                  >
                    {fromDate}
                  </span>
                  <input
                    type='time'
                    value={fromTime}
                    onChange={(e) => handleFromTimeChange(e.target.value)}
                    className={cn(
                      'size-p2 w-24 shrink-0 rounded-md px-2 py-1.5',
                      'border-border-primary bg-surface-primary border',
                      `text-primary focus:border-border-focus
                      focus:outline-none`
                    )}
                  />
                </div>

                <div
                  className='grid grid-cols-[4rem_minmax(0,1fr)_auto]
                    items-center gap-2'
                >
                  <span className='size-p2 text-secondary'>Till</span>
                  <span
                    className='size-p2 text-primary min-w-0 truncate font-bold'
                  >
                    {tillDate}
                  </span>
                  <input
                    type='time'
                    value={toTime}
                    onChange={(e) => handleToTimeChange(e.target.value)}
                    className={cn(
                      'size-p2 w-24 shrink-0 rounded-md px-2 py-1.5',
                      'border-border-primary bg-surface-primary border',
                      `text-primary focus:border-border-focus
                      focus:outline-none`
                    )}
                  />
                </div>
              </div>

              {showOrderError && (
                <p className='size-caption text-negative mt-2'>
                  &quot;From&quot; must be before &quot;To&quot;
                </p>
              )}

              <button
                disabled={isApplyDisabled}
                onClick={applyCustomRange}
                className={cn(
                  'size-p2 mt-2.5 w-full rounded-lg px-3 py-2 font-medium',
                  'bg-surface-focus text-inverted cursor-pointer',
                  'transition-colors hover:brightness-110',
                  'disabled:bg-surface-disabled disabled:text-disabled',
                  'disabled:cursor-not-allowed'
                )}
              >
                Apply
              </button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
});
